import { useRouter } from 'next/router'
import { useState } from 'react'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { Dialog } from 'primereact/dialog'
import { SubscriptionPlanType } from '@/types'
import { HeaderBar } from '@/components/HeaderBar'
import { Footer } from '@/components/Footer'
import { SEO } from '@/components/SEO'
import styles from '../styles/Home.module.scss'
import { Flex } from '@fluentui/react-northstar'
import { useSession } from 'next-auth/react'
import { useTeamsMobile } from '@/hooks/useTeamsMobile'

// Import shared components
import { PricingPlans, BillingToggle } from '@/components/subscriptions/PricingPlans'
import { PlanComparisonTable } from '@/components/subscriptions/ComparisonTable'
import { PricingFAQ } from '@/components/subscriptions/FAQ'

const Pricing = () => {
  const router = useRouter()
  const { data: session, status } = useSession()
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanType | null>(null)
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly') // Default to yearly since it's better value
  const isMobile = useTeamsMobile()

  const handleUpgradeClick = (plan: SubscriptionPlanType) => {
    if (!session) {
      setSelectedPlan(plan)
      setShowLoginDialog(true)
      return
    }

    // If user is logged in, redirect to subscription page
    router.push({
      pathname: '/account/subscription',
      query: { plan, billing: billingInterval },
    })
  }

  const handleLoginClick = () => {
    router.push({
      pathname: '/login',
      query: {
        callbackUrl: `/account/subscription?plan=${selectedPlan}&billing=${billingInterval}`,
      },
    })
  }

  if (isMobile) {
    return (
      <div className={styles.wrapper}>
        <HeaderBar />
        <div className={styles.container}>
          <Flex column as="main" hAlign="center">
            <div className="p-4 max-w-2xl w-full m-auto text-center">
              <h1 className="text-2xl font-bold mb-4 dark:text-white">
                Desktop or web required
              </h1>
              <p className="text-lg dark:text-gray-200">
                This area is available from Delayed Send on desktop or web.
              </p>
            </div>
          </Flex>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <SEO 
        title="Delayed Send Pricing | Free & Premium Plans for Microsoft Teams" 
        description="Choose from Delayed Send's free, Pro, and Team plans. Schedule unlimited messages, attach files, and use recurring messages with our premium plans."
        keywords="Microsoft Teams, delayed messages, pricing, subscription, pro plan, team plan, scheduler"
      />
      <HeaderBar />
      <div className={styles.container}>
        <Flex column as="main" hAlign="center">
          <div className="p-4 max-w-6xl w-full m-auto">
            <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
              Simple, Transparent Pricing
            </h1>
            <p className="text-center text-lg mb-8 dark:text-gray-200">
              Choose the plan that works best for you or your team
            </p>

            {/* Billing Interval Toggle */}
            <BillingToggle
              billingInterval={billingInterval}
              setBillingInterval={setBillingInterval}
            />

            {/* Pricing Plans */}
            <PricingPlans
              billingInterval={billingInterval}
              onUpgradeClick={handleUpgradeClick}
            />

            {/* Feature Comparison Table */}
            <Divider className="my-8 dark:bg-gray-700" />
            <h2 className="text-xl font-bold mb-4 text-center dark:text-white">
              Plan Comparison
            </h2>
            <PlanComparisonTable />

            {/* FAQ Section */}
            <Divider className="my-8 dark:bg-gray-700" />
            <h2 className="text-xl font-bold mb-4 text-center dark:text-white">
              Frequently Asked Questions
            </h2>
            <div className="mb-6">
              <PricingFAQ />
            </div>
          </div>
        </Flex>
      </div>

      {/* Login Dialog */}
      <Dialog
        header="Sign in to Continue"
        visible={showLoginDialog}
        style={{ width: '450px' }}
        onHide={() => setShowLoginDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowLoginDialog(false)}
            />
            <Button label="Sign In" icon="pi pi-sign-in" onClick={handleLoginClick} />
          </div>
        }
      >
        <div className="p-4">
          <p className="mb-4">
            Please sign in to start your 30-day free trial of the {selectedPlan} plan.
          </p>
          <p className="text-sm text-gray-600">
            You'll need to have a Microsoft account to complete the subscription process.
          </p>
        </div>
      </Dialog>
      <Footer />
    </div>
  )
}

export default Pricing
